import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	ArrowRightCircle,
	Phone,
	MessageSquare,
	Video,
	ArrowLeft,
	ShieldCheck,
} from 'lucide-react';
import { Button, Input, InputWrapperAuth } from '@/components/ui';
import { maskPhone, removeMask } from '@/utils';
import { getStepData, saveStepData } from '@/utils/localStorageUtils';
import { STORAGE_KEYS } from './StepOne';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';

const schema = z.object({
	phone_number: z
		.string()
		.transform(removeMask)
		.refine(
			(val) => !val || val.length === 11,
			'Número de telefone deve ter 11 dígitos',
		)
		.refine(
			(val) => !val || /^\d+$/.test(val),
			'Número de telefone deve conter apenas números',
		)
		.optional(),
	brief_description: z
		.string()
		.max(255, 'Máximo de 255 caracteres')
		.optional(),
	video_url: z
		.string()
		.superRefine((val, ctx) => {
			if (val && val.length > 0) {
				try {
					new URL(val);
				} catch {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'URL inválida',
					});
				}
			}
		})
		.optional(),
});

type FormData = z.infer<typeof schema>;

// Componente de Label interativo padronizado
const FormLabel = ({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) => (
	<label
		htmlFor={htmlFor}
		className="mb-1 ml-1 block cursor-pointer text-[10px] font-black uppercase tracking-widest text-stone-500 transition-colors hover:text-stone-700"
	>
		{children}
	</label>
);

const StepThree = () => {
	const navigate = useNavigate();
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	useEffect(() => {
		const savedData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_CONTACT,
		);
		if (savedData) {
			Object.entries(savedData).forEach(([key, value]) => {
				setValue(key as keyof FormData, value as string);
			});
		}
	}, [setValue]);

	const onSubmit = (data: FormData) => {
		saveStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_CONTACT,
			data,
		);
		navigate('/profissional/cadastro/info-modalidade');
	};

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Contato e Vídeo | Plumaa</title>
			</Helmet>
			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					{/* TÍTULOS */}
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Contato e Vídeo
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Etapa 3 de 4: Informações de contato e apresentação
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-4">
							{/* TELEFONE */}
							<div>
								<FormLabel htmlFor="field-phone">
									Telefone (opcional)
								</FormLabel>
								<InputWrapperAuth
									icon={<Phone className={iconCommonClass} />}
									error={errors.phone_number?.message}
									value={watch('phone_number')}
									displayError
								>
									<Input
										id="field-phone"
										maxLength={15}
										value={maskPhone(
											watch('phone_number') || '',
										)}
										{...register('phone_number')}
										placeholder="(00) 00000-0000"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							{/* DESCRIÇÃO */}
							<div>
								<FormLabel htmlFor="field-description">
									Breve descrição (opcional)
								</FormLabel>
								<InputWrapperAuth
									icon={
										<MessageSquare
											className={iconCommonClass}
										/>
									}
									error={errors.brief_description?.message}
									value={watch('brief_description')}
									displayError
								>
									<Input
										id="field-description"
										{...register('brief_description')}
										placeholder="Fale um pouco sobre você como profissional"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							{/* URL DO VÍDEO */}
							<div>
								<FormLabel htmlFor="field-video">
									URL do vídeo (opcional)
								</FormLabel>
								<InputWrapperAuth
									icon={<Video className={iconCommonClass} />}
									error={errors.video_url?.message}
									value={watch('video_url')}
									displayError
								>
									<Input
										id="field-video"
										{...register('video_url')}
										placeholder="https://exemplo.com/video"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						{/* BOTÕES DE AÇÃO */}
						<div className="space-y-4 pt-2">
							<Button
								type="submit"
								className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
								text="Próxima etapa"
								endIcon={
									<ArrowRightCircle
										size={14}
										className="ml-1"
									/>
								}
							/>

							<div className="text-center">
								<button
									type="button"
									onClick={() => navigate(-1)}
									className="mx-auto flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-400 transition-colors hover:text-stone-900"
								>
									<ArrowLeft size={12} />
									Voltar para etapa anterior
								</button>
							</div>
						</div>

						{/* INFO DE SEGURANÇA */}
						<div className="mt-2 flex items-center justify-center gap-2 border-t border-stone-50 pt-4 text-[9px] font-black uppercase tracking-widest text-stone-300">
							<ShieldCheck size={14} />
							<span>Seus dados estão protegidos pela Plumaa</span>
						</div>
					</form>
				</div>
			</main>
		</div>
	);
};

export { StepThree };
